import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from '@services/app.service';
import { ConfigService } from '@services/config.service';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { CartStateService } from '@services/cartStateService';
import { Subject, takeUntil } from 'rxjs';
import { AuthStateService } from '@guards/auth-state.service';

@Component({
  selector: 'app-variant-view',
  templateUrl: './variant-view.component.html',
  styleUrls: ['./variant-view.component.scss']
})
export class VariantViewComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  product: any = null;
  variants: any[] = [];
  endpoint = '';

  isLoginModalOpen = false;
  isRegisterModalOpen = false;

  loginStep: 'email' | 'otp' = 'email';
  loginEmail = '';
  loginOtp = '';
  isLoginOtpValid = false;
  loginError = '';

  registerStep: 'email' | 'otp' | 'details' = 'email';
  registerEmail = '';
  registerOtp = '';
  isRegisterOtpValid = false;
  firstName = '';
  phoneNumber = '';
  registerError = '';

  isSubmitting = false;

  pendingCartItem: {
    productVariantId: number;
    quantity: number;
  } | null = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private appService: AppService,
    private toastr: ToastrService,
    private config: ConfigService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private cartState: CartStateService,
    private authState: AuthStateService
  ) {}

  ngOnInit(): void {
    this.endpoint = this.config.getConfig().END_POINT;

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (params) => {
          if (!params['product']) {
            this.product = null;
            this.variants = [];
            return;
          }

          try {
            const productId = Number(
              JSON.parse(atob(params['product']))
            );

            if (Number.isInteger(productId) && productId > 0) {
              this.getProductDetails(productId);
            } else {
              this.toastr.error('Invalid product ID');
            }
          } catch {
            this.toastr.error('Invalid product information');
          }
        }
      });
  }

  getProductDetails(id: number): void {
    this.spinner.show();

    this.appService.getWebsiteProductById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (product: any) => {
          this.product = product;
          this.variants = (product?.variants || [])
            .filter((variant: any) => variant.isAvailable === true);

          this.spinner.hide();
        },
        error: () => {
          this.spinner.hide();
          this.toastr.error('Failed to load product variants');
        }
      });
  }

  getImageUrl(variant: any): string {
    const defaultImage = 'assets/img/my-1.jpg';

    if (!Array.isArray(variant?.images) || !variant.images.length) {
      return defaultImage;
    }

    const primaryImage = variant.images.find(
      (image: any) =>
        image.isPrimary === true &&
        image.isActive === true &&
        image.imageDetailsId
    );

    const activeImage = variant.images.find(
      (image: any) =>
        image.isActive === true && image.imageDetailsId
    );

    const imageId =
      primaryImage?.imageDetailsId || activeImage?.imageDetailsId;

    return imageId
      ? `${this.endpoint}/api/product/image-details/get/${imageId}`
      : defaultImage;
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.onerror = null;
    img.src = 'assets/img/my-1.jpg';
  }

  navigateToDetailView(variantId: number): void {
    if (!this.product?.productId || !variantId) {
      return;
    }

    const encodedProduct = btoa(
      JSON.stringify(this.product.productId)
    );

    this.router.navigate(['/detail-view'], {
      queryParams: {
        product: encodedProduct,
        variantId
      }
    });

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  // ================= ADD TO CART =================

  addToCart(variant: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (!variant?.productVariantId) {
      this.toastr.error('Invalid product variant');
      return;
    }

    const payload = {
      productVariantId: Number(variant.productVariantId),
      quantity: 1
    };

    const userId = localStorage.getItem('userId');
    const authToken = localStorage.getItem('authToken');

    // Require both user ID and authentication token.
    if (!userId || !authToken) {
      this.pendingCartItem = payload;
      this.openLoginModal();
      return;
    }

    this.processAddToCart(payload);
  }

  processAddToCart(payload: {
    productVariantId: number;
    quantity: number;
  }): void {
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.spinner.show();

    this.appService.addToCart(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.spinner.hide();

          this.cartState.updateItemQuantity(
            payload.productVariantId,
            payload.quantity
          );

          this.toastr.success('Product added to cart');
        },
        error: (err: any) => {
          this.isSubmitting = false;
          this.spinner.hide();

          const message =
            err?.error?.message || 'Failed to add product to cart';

          this.toastr.error(message);
        }
      });
  }

  // ================= MODALS =================

  openLoginModal(): void {
    this.isRegisterModalOpen = false;
    this.isLoginModalOpen = true;

    document.body.classList.add('modal-open');
  }

  closeLoginModal(): void {
    this.isLoginModalOpen = false;
    this.updateBodyModalState();
  }

  openRegisterModal(): void {
    this.isLoginModalOpen = false;
    this.isRegisterModalOpen = true;

    document.body.classList.add('modal-open');
  }

  closeRegisterModal(): void {
    this.isRegisterModalOpen = false;
    this.updateBodyModalState();
  }

  private updateBodyModalState(): void {
    if (!this.isLoginModalOpen && !this.isRegisterModalOpen) {
      document.body.classList.remove('modal-open');
    }
  }

  switchToLogin(): void {
    this.resetLogin();
    this.closeRegisterModal();
    this.openLoginModal();
  }

  switchToRegister(): void {
    this.resetRegister();
    this.closeLoginModal();
    this.openRegisterModal();
  }

  // ================= VALIDATIONS =================

  isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      (email || '').trim()
    );
  }

  isValidPhone(phone: string): boolean {
    return /^[0-9]{10}$/.test(phone || '');
  }

checkLoginOtp(): boolean {
  this.loginOtp = (this.loginOtp || '').replace(/\D/g, '').slice(0, 4);
  this.isLoginOtpValid = /^\d{4}$/.test(this.loginOtp);

  if (!this.isLoginOtpValid) {
    this.loginError = 'Please enter a valid 4-digit OTP.';
    return false;
  }

  this.loginError = '';
  return true;
}

checkRegisterOtp(): boolean {
  this.registerOtp = (this.registerOtp || '').replace(/\D/g, '').slice(0, 4);
  this.isRegisterOtpValid = /^\d{4}$/.test(this.registerOtp);

  if (!this.isRegisterOtpValid) {
    this.registerError = 'Please enter a valid 4-digit OTP.';
    return false;
  }

  this.registerError = '';
  return true;
}

onLoginOtpInput(event: Event): void {
  const input = event.target as HTMLInputElement;

  this.loginOtp = input.value.replace(/\D/g, '').slice(0, 4);
  input.value = this.loginOtp;
  this.isLoginOtpValid = /^\d{4}$/.test(this.loginOtp);
  this.loginError = '';
}

onRegisterOtpInput(event: Event): void {
  const input = event.target as HTMLInputElement;

  this.registerOtp = input.value.replace(/\D/g, '').slice(0, 4);
  input.value = this.registerOtp;
  this.isRegisterOtpValid = /^\d{4}$/.test(this.registerOtp);
  this.registerError = '';
}

  resetLogin(): void {
    this.loginStep = 'email';
    this.loginEmail = '';
    this.loginOtp = '';
    this.isLoginOtpValid = false;
    this.loginError = '';
  }

  resetRegister(): void {
    this.registerStep = 'email';
    this.registerEmail = '';
    this.registerOtp = '';
    this.isRegisterOtpValid = false;
    this.firstName = '';
    this.phoneNumber = '';
    this.registerError = '';
  }

  // ================= LOGIN OTP =================

  loginSendOtpApi(): void {
    const email = this.loginEmail.trim().toLowerCase();

    if (!this.isValidEmail(email)) {
      this.loginError = 'Enter a valid email address';
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.loginError = '';
    this.isSubmitting = true;
    this.spinner.show();

    this.appService.loginSendOtp({ email })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.spinner.hide();

          this.loginEmail = email;
          this.loginStep = 'otp';
        },
        error: (err: any) => {
          this.isSubmitting = false;
          this.spinner.hide();

          this.loginError =
            err?.error?.message || 'Failed to send OTP';
        }
      });
  }

  loginVerifyOtpApi(): void {
  if (this.isSubmitting) {
    return;
  }

  const email = (this.loginEmail || '').trim().toLowerCase();

  if (!this.isValidEmail(email)) {
    this.loginError = 'Please enter a valid email address.';
    return;
  }

  // Stop if the OTP is not exactly four digits.
  if (!this.checkLoginOtp()) {
    return;
  }

  this.loginError = '';
  this.isSubmitting = true;
  this.spinner.show();

  this.appService.loginVerifyOtp({
    email: email,
    otp: this.loginOtp
  })
  .pipe(takeUntil(this.destroy$))
  .subscribe({
    next: (res: any) => {
      this.spinner.hide();
      this.isSubmitting = false;

      const login = res?.login;
      const userId = login?.loginId;
      const token = res?.authHeader;

      if (userId == null || !token) {
        this.loginError =
          'Login response is missing user ID or authentication token.';
        return;
      }

      // Save login details.
      localStorage.setItem('authToken', token);
      localStorage.setItem('userId', String(userId));
      localStorage.setItem('userType', login.userType || 'USER');

      this.authState.setUser({
        userId: userId,
        userType: login.userType || 'USER'
      });

      this.toastr.success('Login successful');

      this.closeLoginModal();

      // Add the previously selected product to the cart.
      this.addPendingItemToCart();
    },

    error: (err: any) => {
      this.spinner.hide();
      this.isSubmitting = false;

      this.loginError =
        err?.error?.message || 'Invalid OTP. Please try again.';
    }
  });
}

  // ================= REGISTRATION OTP =================

  registrationSendOtpApi(): void {
    const email = this.registerEmail.trim().toLowerCase();

    if (!this.isValidEmail(email)) {
      this.registerError = 'Enter a valid email address';
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.registerError = '';
    this.isSubmitting = true;
    this.spinner.show();

    this.appService.registrationSendOtp({ email })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.spinner.hide();
          this.isSubmitting = false;

          this.registerEmail = email;
          this.registerStep = 'otp';
        },
        error: (err: any) => {
          this.spinner.hide();
          this.isSubmitting = false;

          this.registerError =
            err?.error?.message || 'Failed to send OTP';
        }
      });
  }

  registrationVerifyOtpApi(): void {
  if (this.isSubmitting) {
    return;
  }

  const email = (this.registerEmail || '').trim().toLowerCase();

  if (!this.isValidEmail(email)) {
    this.registerError = 'Please enter a valid email address.';
    return;
  }

  // Stop if the OTP is not exactly four digits.
  if (!this.checkRegisterOtp()) {
    return;
  }

  this.registerError = '';
  this.isSubmitting = true;
  this.spinner.show();

  this.appService.registrationVerifyOtp({
    email: email,
    otp: this.registerOtp
  })
  .pipe(takeUntil(this.destroy$))
  .subscribe({
    next: () => {
      this.spinner.hide();
      this.isSubmitting = false;

      // Move to the registration details step after OTP verification.
      this.isRegisterOtpValid = true;
      this.registerStep = 'details';

      this.toastr.success('Email verified successfully');
    },

    error: (err: any) => {
      this.spinner.hide();
      this.isSubmitting = false;

      this.registerError =
        err?.error?.message || 'Invalid OTP. Please try again.';
    }
  });
}

  completeRegistration(): void {
    const name = this.firstName.trim();
    const phone = this.phoneNumber.trim();

    if (name.length < 2 || !this.isValidPhone(phone)) {
      this.registerError =
        'Enter a valid first name and 10-digit phone number';
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.registerError = '';
    this.isSubmitting = true;
    this.spinner.show();

    const payload = {
      name,
      email: this.registerEmail.trim().toLowerCase(),
      userType: 'USER',
      phoneNumber: phone,
      whatsappNumber: phone,
      password: '',
      countryCode: '91'
    };

    this.appService.saveRegistration(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.spinner.hide();
          this.isSubmitting = false;

          const login = res?.login;
          const userId = login?.loginId;
          const token = res?.authHeader;

          if (userId == null || !token) {
            this.registerError =
              'Registration response is missing user ID or token';
            return;
          }

          localStorage.setItem('authToken', token);
          localStorage.setItem('userId', String(userId));
          localStorage.setItem('userType', login.userType || 'USER');

          this.authState.setUser({
            userId,
            userType: login.userType
          });

          this.toastr.success('Registration successful');
          this.closeRegisterModal();

          this.addPendingItemToCart();
        },
        error: (err: any) => {
          this.spinner.hide();
          this.isSubmitting = false;

          this.registerError =
            err?.error?.message || 'Registration failed';
        }
      });
  }

  private addPendingItemToCart(): void {
    const item = this.pendingCartItem;

    if (!item) {
      return;
    }

    // Clear first so the same item is not accidentally added twice.
    this.pendingCartItem = null;
    this.processAddToCart(item);
  }

  ngOnDestroy(): void {
    this.spinner.hide();
    document.body.classList.remove('modal-open');

    this.destroy$.next();
    this.destroy$.complete();
  }
}

